import { Button, ButtonType } from '@ardoq/button';
import {
  ButtonWithDropdown,
  DropdownItem,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { AddIcon, IconSize, WorkspaceIcon } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { APISurveyAttributes } from '@ardoq/api-types';
import { WithPopover } from '@ardoq/popovers';
import { type I18nLocale, t } from '@ardoq/i18n';

const getSurveyOptions = (
  surveys: APISurveyAttributes[],
  workspaceNameBySurveyId: Record<string, string>,
  onSurveyClick: (surveyId: string) => void,
  i18nLocale: I18nLocale
): DropdownItem[] => {
  return surveys.map(survey => ({
    label: survey.componentTypeName,
    description: (
      <FlexBox gap="xsmall">
        <WorkspaceIcon iconSize={IconSize.SMALL} />
        {t(
          i18nLocale,
          'inventory:surveyButton.inWorkspace',
          {
            workspaceName:
              workspaceNameBySurveyId[survey._id] ||
              t(
                i18nLocale,
                'inventory:surveyButton.unknownWorkspace',
                'Unknown workspace'
              ),
          },
          'In {{workspaceName}} Workspace'
        )}
      </FlexBox>
    ),
    truncateTitle: true,
    onClick: () => onSurveyClick(survey._id),
    type: DropdownOptionType.BUTTON_OPTION,
  }));
};

type CreateComponentFromSurveyButtonProps = {
  openSurvey: (surveyId: string) => void;
  surveys: APISurveyAttributes[];
  workspaceNameBySurveyId: Record<string, string>;
  i18nLocale: I18nLocale;
};

export const CreateComponentFromSurveyButton = ({
  openSurvey,
  surveys,
  workspaceNameBySurveyId,
  i18nLocale,
}: CreateComponentFromSurveyButtonProps) => {
  if (surveys.length === 0) {
    return (
      <WithPopover
        content={t(
          i18nLocale,
          'inventory:surveyButton.noSurveyTooltip',
          "There is either no survey to document a new component or you don't have access to submit survey answers"
        )}
      >
        <Button buttonType={ButtonType.PRIMARY} isDisabled={true}>
          <AddIcon />
          {t(
            i18nLocale,
            'inventory:surveyButton.documentNewComponent',
            'Document new component'
          )}
        </Button>
      </WithPopover>
    );
  }
  if (surveys.length === 1) {
    return (
      <Button
        buttonType={ButtonType.PRIMARY}
        onClick={() => openSurvey(surveys[0]._id)}
      >
        <AddIcon />
        {t(
          i18nLocale,
          'inventory:surveyButton.documentNewType',
          { typeName: surveys[0].componentTypeName },
          'Document new {{typeName}}'
        )}
      </Button>
    );
  }

  return (
    <ButtonWithDropdown
      dropdownSize={DropdownSize.AUTO}
      alignMenuTo="left"
      dataTestId="surveys-dropdown"
      options={getSurveyOptions(
        surveys,
        workspaceNameBySurveyId,
        openSurvey,
        i18nLocale
      )}
      buttonType={ButtonType.PRIMARY}
    >
      <AddIcon />
      {t(i18nLocale, 'inventory:surveyButton.documentNew', 'Document new')}
    </ButtonWithDropdown>
  );
};
